// SpecialContributionPrograms.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  LinearProgress,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchPrograms,
  createProgram,
  approveProgram,
  approveWithdrawal,
  createWithdrawalRequest,
  fetchProgramParticipants,
} from "../services/specialContributionService";
import { fetchMembers as fetchGroupMembers } from "../services/memberService";
import { fetchGroupTerms } from "../services/groupService";
import { useTranslation } from "react-i18next";
import formatCurrency from "../shared/formatCurrency";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

// Utility for status chips
const statusChipProps = (program, t) => {
  switch (program.status) {
    case "ACTIVE":
      return { label: t("Active"), color: "success" };
    case "PENDING_CHAIR_APPROVAL":
      return { label: t("Pending Chair Approval"), color: "warning" };
    case "PENDING_SECRETARY_APPROVAL":
      return { label: t("Pending Secretary Approval"), color: "warning" };
    case "PENDING_ACCOUNTANT_APPROVAL":
      return { label: t("Pending Accountant Approval"), color: "warning" };
    case "CLOSED_TARGET_REACHED":
      return { label: t("Closed (Target Reached)"), color: "primary" };
    case "CLOSED_TIMEFRAME_ENDED":
      return { label: t("Closed (Time Ended)"), color: "default" };
    case "REJECTED":
      return { label: t("Rejected"), color: "error" };
    default:
      return { label: t(program.status), color: "default" };
  }
};

const withdrawalChipProps = (status, t) => {
  switch (status) {
    case "NOT_REQUESTED":
      return { label: t("Not Requested"), color: "default" };
    case "PENDING_CHAIR_APPROVAL":
      return { label: t("Pending Chair Approval"), color: "warning" };
    case "PENDING_SECRETARY_APPROVAL":
      return { label: t("Pending Secretary Approval"), color: "warning" };
    case "PENDING_ACCOUNTANT_APPROVAL":
      return { label: t("Pending Accountant Approval"), color: "warning" };
    case "REJECTED":
      return { label: t("Withdrawal Rejected"), color: "error" };
    case "APPROVED":
      return { label: t("Funds Released"), color: "success" };
    default:
      return { label: t(status), color: "default" };
  }
};

// ---------------- MAIN COMPONENT ----------------
export default function SpecialContributionPrograms() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const { user } = useAuth();
  const [scope, setScope] = useState("all"); // 'all' | 'mine'
  const [search, setSearch] = useState("");
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isWithdrawalDetailOpen, setWithdrawalDetailOpen] = useState(false);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");

  // API Data State
  const [programs, setPrograms] = useState([]);
  const [members, setMembers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // --- Start: Logic from SpecialContributionCreate ---
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [targetPerMember, setTargetPerMember] = useState(30000);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [selectAll, setSelectAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialogError, setDialogError] = useState(null);
  const dialogContentRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload) ? payload : payload?.results || payload?.data || [];
        setTerms(list);
      })
      .catch((err) => console.error("Failed to load terms:", err));
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);

  // Fetch initial programs
  const loadPrograms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const termFilter = effectiveTermId ? { term_id: effectiveTermId } : {};
      const data = await fetchPrograms(termFilter);
      setPrograms(data);
      if (data.length > 0) {
        const hasSelected = selectedProgramId && data.some((p) => p.id === selectedProgramId);
        setSelectedProgramId(hasSelected ? selectedProgramId : data[0].id);
      } else {
        setSelectedProgramId(null);
      }
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: "error", title: t("Failed to load programs"), text: err.message, confirmButtonColor: brand });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTermId === "current" && !effectiveTermId) {
      return undefined;
    }
    loadPrograms();
  }, [effectiveTermId, selectedTermId]);

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    const base = members;
    if (!q) return base;
    return base.filter(m =>
      [m.user.full_name, m.user.phone_number, m?.role].some(x => x.toLowerCase().includes(q))
    );
  }, [memberQuery, members]);

  useEffect(() => {
    if (selectAll) {
      setSelectedIds(members.filter(m => m.is_active).map(m => m.id));
    }
  }, [selectAll, members]);

  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectedMembers = useMemo(() => members.filter(m => selectedIds.includes(m.id)), [members, selectedIds]);
  const selectedActiveCount = useMemo(() => selectedMembers.filter(m => m.is_active).length, [selectedMembers]);
  const totalTarget = (Number(targetPerMember) || 0) * selectedActiveCount;

  const showDialogError = (title, text) => {
    setDialogError({ title, text });
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const notifyError = (title, text, icon = "warning") => {
    if (isCreateDialogOpen) {
      showDialogError(title, text);
      return;
    }
    Swal.fire({ icon, title, text, confirmButtonColor: brand });
  };

  const validate = () => {
    if (!user?.member_is_active) {
      notifyError(t("Initiator inactive"), t("Only active members can initiate a program."), "error");
      return false;
    }
    if (!title.trim()) {
      notifyError(t("Missing Title"), t("Please enter a program title."));
      return false;
    }
    // Backend handles duplicate title validation
    if (!targetPerMember || Number(targetPerMember) <= 0) {
      notifyError(t("Invalid Target"), t("Target per member must be greater than zero."));
      return false;
    }
    if (!start || !end) {
      notifyError(t("Missing Dates"), t("Please select start and end dates."));
      return false;
    }
    if (new Date(end) <= new Date(start)) {
      notifyError(t("Invalid Timeframe"), t("End date must be after start date."));
      return false;
    }
    if (selectedActiveCount === 0) {
      notifyError(t("No Participants"), t("Select at least one active participant."));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setDialogError(null);

    const payload = {
      title: title.trim(),
      description: desc.trim(),
      target_per_member: targetPerMember,
      start_date: start,
      end_date: end,
      participant_ids: selectedIds,
    };

    try {
      await createProgram(payload);
      Swal.fire({
        icon: "success",
        title: t("Program Created"),
        html: `
          <div style="text-align:left">
            <strong>${t("Title *")}:</strong> ${payload.title}<br/>
            <strong>${t("Leader:")}</strong> ${user.member_full_name}<br/>
            <strong>${t("Participants (active):")}</strong> ${selectedActiveCount}<br/>
            <strong>${t("Target/Member:")}</strong> ${formatCurrency(payload.target_per_member, locale)} TZS<br/>
            <strong>${t("Total Target:")}</strong> ${formatCurrency(totalTarget, locale)} TZS<br/>
            <hr style="margin:8px 0"/>
            <small>${t("Program now pending approval from the Chairperson.")}</small>
          </div>
        `,
        confirmButtonColor: brand,
      });
      setCreateDialogOpen(false);
      loadPrograms(); // Refetch programs list
    } catch (err) {
      notifyError(t("Creation Failed"), err.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setTitle("");
    setDesc("");
    setTargetPerMember(30000);
    setStart("");
    setEnd("");
    setMemberQuery("");
    setSelectAll(true);
    setSelectedIds(members.filter(m => m.is_active).map(m => m.id));
  };

  const handleOpenCreateDialog = () => {
    resetCreateForm();
    setDialogError(null);
    setCreateDialogOpen(true);
  };

  // Fetch members when create dialog opens
  useEffect(() => {
    if (isCreateDialogOpen && members.length === 0) {
      const loadMembers = async () => {
        try {
          const data = await fetchGroupMembers();
          setMembers(data);
          setSelectedIds(data.filter(m => m.is_active).map(m => m.id));
        } catch (err) {
          notifyError(t("Could not load members"), err.message, "error");
        }
      };
      loadMembers();
    }
  }, [isCreateDialogOpen, members.length]);

  const handleCloseCreateDialog = () => {
    setDialogError(null);
    setCreateDialogOpen(false);
  };
  // --- End: Logic from SpecialContributionCreate ---

  const handleOpenWithdrawalDetail = () => {
    setWithdrawalDetailOpen(true);
  };

  const handleCloseWithdrawalDetail = () => {
    setWithdrawalDetailOpen(false);
  };

  const filteredPrograms = useMemo(() => {
    let list = programs;
    if (scope === "mine") {
      list = list.filter((p) => p.leader === user?.member_id);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.leader_name.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [scope, search, programs, user]);

  const selectedProgram = useMemo(
    () => filteredPrograms.find((p) => p.id === selectedProgramId) || filteredPrograms[0] || null,
    [filteredPrograms, selectedProgramId]
  );

  // Fetch participants when a program is selected
  useEffect(() => {
    if (selectedProgramId) {
      const loadParticipants = async () => {
        try {
          const data = await fetchProgramParticipants(selectedProgramId);
          setParticipants(data);
        } catch (err) {
          // Silently fail or show a small toast
          console.error("Failed to load participants:", err);
        }
      };
      loadParticipants();
    }
  }, [selectedProgramId]);

  const handleScopeChange = (_, value) => {
    if (value) setScope(value);
  };

  const handleRequestWithdrawal = async () => {
    if (!selectedProgram) return;

    const isClosed = selectedProgram.status.startsWith("CLOSED");
    if (!isClosed) {
      Swal.fire({
        icon: "info",
        title: t("Program still active"),
        text: t("Withdrawal can only be initiated after the program closes."),
        confirmButtonColor: brand,
      });
      return;
    }

    if (selectedProgram.withdrawal_request) {
      Swal.fire({
        icon: "info",
        title: t("Request Already Exists"),
        text: t("Withdrawal request exists status", { status: selectedProgram.withdrawal_request.status }),
        confirmButtonColor: brand,
      });
      return;
    }

    const { value: formValues } = await Swal.fire({
      icon: "question",
      title: t("Request Withdrawal?"),
      html: `
        <p>${t("Enter beneficiary details for the withdrawable amount of {{amount}} TZS.", {
        amount: formatCurrency(selectedProgram.total_withdrawable_amount, locale),
      })}</p>
        <input id="swal-beneficiary-name" class="swal2-input" placeholder="${t("Beneficiary Name")}">
        <input id="swal-beneficiary-account" class="swal2-input" placeholder="${t("Beneficiary Account (Bank/Mobile)")}">
        <textarea id="swal-purpose" class="swal2-textarea" placeholder="${t("Purpose of withdrawal (optional)")}"></textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: t("Submit Request"),
      confirmButtonColor: brand,
      preConfirm: () => {
        const name = document.getElementById("swal-beneficiary-name").value;
        const account = document.getElementById("swal-beneficiary-account").value;
        if (!name || !account) {
          Swal.showValidationMessage(t("Beneficiary name and account are required."));
          return false;
        }
        return {
          beneficiary_name: name,
          beneficiary_account: account,
          purpose: document.getElementById("swal-purpose").value,
        };
      },
    });

    if (!formValues) return;

    const payload = {
      program: selectedProgram.id,
      amount: selectedProgram.total_withdrawable_amount,
      ...formValues,
    };

    try {
      await createWithdrawalRequest(payload);
      Swal.fire({ icon: "success", title: t("Withdrawal Request Sent!"), text: t("Withdrawal pending chairperson"), confirmButtonColor: brand });
      loadPrograms(); // Refresh to show the new withdrawal status
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Request Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  const handleProgramApprovalAction = async (isApprove) => {
    if (!selectedProgram) return;

    const isApprover = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(user?.role);
    const isPending = selectedProgram.status.startsWith("PENDING");

    if (!isApprover) {
      Swal.fire({
        icon: "error",
        title: t("Not Allowed"),
        text: t("Only group leadership (Chair, Secretary, Accountant) can perform this action."),
        confirmButtonColor: brand,
      });
      return;
    }

    if (!isPending) {
      if (!isPending) {
        Swal.fire({ icon: "info", title: t("Action Not Applicable"), text: t("This program is not in a pending state."), confirmButtonColor: brand });
        return;
      }
      return;
    }

    const result = await Swal.fire({
      icon: isApprove ? "question" : "warning",
      title: isApprove ? t("Approve Program Creation?") : t("Reject Program Creation?"),
      text: isApprove ? t("This will move the program to the next approval stage.") : t("This action will stop the program creation."),
      input: !isApprove ? "text" : undefined,
      inputLabel: !isApprove ? t("Reason for rejection (required)") : undefined,
      inputPlaceholder: !isApprove ? t("Enter reason...") : undefined,
      inputValidator: (value) => !isApprove && !value && t("You must provide a reason for rejection."),
      showCancelButton: true,
      confirmButtonText: isApprove ? t("Approve") : t("Reject"),
      confirmButtonColor: isApprove ? brand : "#c62828",
    });

    if (!result.isConfirmed) return;

    try {
      await approveProgram(selectedProgram.id, result.value || "");
      Swal.fire({ icon: "success", title: t("Action Successful"), text: isApprove ? t("Program has been approved.") : t("Program has been rejected."), confirmButtonColor: brand });
      loadPrograms(); // Refresh list to show new status
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Action Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  const handleWithdrawalApprovalAction = async (isApprove) => {
    if (!selectedProgram || !selectedProgram.withdrawal_request) return;

    const isApprover = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(user?.role);
    const withdrawalStatus = selectedProgram.withdrawal_request.status;

    if (!isApprover) {
      Swal.fire({
        icon: "error",
        title: t("Not Allowed"),
        text: t("Only group leadership (Chair, Secretary, Accountant) can perform this action."),
        confirmButtonColor: brand,
      });
      return;
    }

    // Check if the current user's role matches the pending approval stage
    const expectedStatus = {
      "Mwenyekiti": "PENDING_CHAIR_APPROVAL",
      "Katibu": "PENDING_SECRETARY_APPROVAL",
      "Muhasibu": "PENDING_ACCOUNTANT_APPROVAL",
    }[user?.role];

    if (withdrawalStatus !== expectedStatus) {
      Swal.fire({ icon: "info", title: t("Action Not Applicable"), text: t("Withdrawal request exists status", { status: withdrawalStatus }), confirmButtonColor: brand });
      return;
    }

    const result = await Swal.fire({
      icon: isApprove ? "question" : "warning",
      title: isApprove ? t("Approve Withdrawal Request?") : t("Reject Withdrawal Request?"),
      text: isApprove ? t("This will move the withdrawal to the next approval stage or release funds.") : t("This action will reject the withdrawal request."),
      input: !isApprove ? "text" : undefined,
      inputLabel: !isApprove ? t("Reason for rejection (required)") : undefined,
      inputPlaceholder: !isApprove ? t("Enter reason...") : undefined,
      inputValidator: (value) => !isApprove && !value && t("You must provide a reason for rejection."),
      showCancelButton: true,
      confirmButtonText: isApprove ? t("Approve") : t("Reject"),
      confirmButtonColor: isApprove ? brand : "#c62828",
    });

    if (!result.isConfirmed) return;

    try {
      await approveWithdrawal(selectedProgram.withdrawal_request.id, result.value || "");
      Swal.fire({ icon: "success", title: t("Action Successful"), text: isApprove ? t("Withdrawal request has been approved.") : t("Withdrawal request has been rejected."), confirmButtonColor: brand });
      loadPrograms(); // Refresh list to show new status
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Action Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" } }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t("Special Contribution Programs")}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t("Monitor program progress, member payments, and handle withdrawals with proper approval chain.")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={scope}
            exclusive
            size="small"
            onChange={handleScopeChange}
          >
            <ToggleButton value="all">{t("All Programs")}</ToggleButton>
            <ToggleButton value="mine">{t("My Programs")}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label={t("Term")}
            value={selectedTermId}
            onChange={(e) => setSelectedTermId(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="current">{t("Current Term")}</MenuItem>
            <MenuItem value="all">{t("All Terms")}</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `Term ${term.term_number}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            sx={{ minWidth: 250 }}
            placeholder={t("Search program or leader...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<GroupAddOutlinedIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Start Program")}
          </Button>
        </Stack>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <GroupOutlinedIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Total Programs")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {filteredPrograms.length}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TimelineIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Active Programs")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {filteredPrograms.filter((p) => p.status === "ACTIVE").length}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <PaidOutlinedIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Closed & Ready for Withdrawal")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {
                      filteredPrograms.filter(
                        (p) =>
                          p.status !== "ACTIVE" &&
                          p.withdrawalStatus !== "RELEASED"
                      ).length
                    }
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Main layout: list + details */}
      <Grid container spacing={2}>
        {/* Left: list */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight={800}>
                  {t("Programs")}
                </Typography>
                <Tooltip title={t("Status based on target amount & timeframe closure.")}>
                  <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Box>
              <Divider />
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Program")}</TableCell>
                      <TableCell>{t("Leader")}</TableCell>
                      <TableCell align="right">{t("Progress")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && (
                      <TableRow><TableCell colSpan={3}><LinearProgress /></TableCell></TableRow>
                    )}
                    {!isLoading && filteredPrograms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          {t("No programs found.")}
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading && filteredPrograms.map((p) => {
                      const progress =
                        p.totalTarget > 0
                          ? Math.round((p.totalCollected / p.totalTarget) * 100)
                          : 0;
                      const chip = statusChipProps(p, t);
                      return (
                        <TableRow
                          key={p.id}
                          hover
                          selected={p.id === selectedProgramId}
                          onClick={() => setSelectedProgramId(p.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {p.title}
                            </Typography>
                            <Chip
                              size="small"
                              label={chip.label}
                              color={chip.color}
                              sx={{ mt: 0.5 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                              <Typography variant="body2">{p.leader_name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 120 }}>
                            <Typography variant="caption" color="text.secondary">
                              {progress}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={progress > 100 ? 100 : progress}
                              sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: details + invoices + withdrawal */}
        <Grid item xs={12} md={7}>
          {selectedProgram ? (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="h6" fontWeight={900}>
                      {selectedProgram.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Leader: <b>{selectedProgram.leader_name}</b> • ID:{" "}
                      <b>{selectedProgram.id}</b>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedProgram.start_date} → {selectedProgram.end_date}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <Chip
                      {...statusChipProps(selectedProgram, t)}
                      size="small"
                    />
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {selectedProgram.withdrawal_request && (
                        <Tooltip title="View Withdrawal Details">
                          <IconButton size="small" onClick={handleOpenWithdrawalDetail}>
                            <VisibilityIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Chip
                        {...withdrawalChipProps(selectedProgram.withdrawal_request?.status || 'NOT_REQUESTED', t)}
                        size="small"
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {selectedProgram.description}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {/* Financial summary */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Target / Member")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {formatCurrency(selectedProgram.target_per_member, locale)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Participants")}: {selectedProgram.participants?.length || '...'}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Total Target")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {formatCurrency(selectedProgram.total_target_amount, locale)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Program-level goal")}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Collected so far")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {formatCurrency(selectedProgram.total_collected, locale)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedProgram.total_target_amount > 0
                          ? `${Math.round(
                            (selectedProgram.total_collected /
                              selectedProgram.total_target_amount) *
                            100
                          )}% of target`
                          : "-"}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Member invoices */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={800}>
                        {t("Member Invoices & Payments")}
                      </Typography>
                      <Tooltip title={t("Each member has a unique reference number for this program.")}>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small">
                        <DownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small">
                        <AutorenewOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("Member")}</TableCell>
                          <TableCell>{t("Reference")}</TableCell>
                          <TableCell align="right">{t("Base Amount")}</TableCell>
                          <TableCell align="right">{t("SSC")}</TableCell>
                          <TableCell align="right">{t("Total Due")}</TableCell>
                          <TableCell align="right">{t("Paid")}</TableCell>
                          <TableCell align="right">{t("Balance")}</TableCell>
                          <TableCell>{t("Status")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              align="center"
                              sx={{ py: 3, color: "text.secondary" }}
                            >
                              {t("No invoices loaded in this demo. In real system, all participants would appear here.")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          participants.map((p, idx) => {
                            const balance = p.total_amount - p.amount_paid;
                            return (
                              <TableRow key={idx} hover>
                                <TableCell>{p.member_name}</TableCell>
                                <TableCell>{p.invoice_reference}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(p.base_amount, locale)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(p.df_amount, locale)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(p.total_amount, locale)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(p.amount_paid, locale)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(balance, locale)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={p.status}
                                    color={
                                      p.status === "PAID"
                                        ? "success"
                                        : p.status === "PARTIAL"
                                          ? "warning"
                                          : "default"
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Withdrawal controls & audit hint */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Typography variant="body2" color="text.secondary">
                      {t("All contributions, SSC, approvals and withdrawals for this program will appear in both member statements and program audit logs to comply with SACCOs & microfinance governance standards.")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={5} display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1}>
                    {(() => {
                      const isPendingCreation = selectedProgram.status.startsWith("PENDING");
                      const isClosed = selectedProgram.status.startsWith("CLOSED");
                      const isLeader = selectedProgram.leader === user?.member_id;
                      const isApprover = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(user?.role);

                      if (isPendingCreation && isApprover) {
                        return (
                          <>
                            <Button variant="outlined" color="success" startIcon={<DoneAllOutlinedIcon />} onClick={() => handleProgramApprovalAction(true)}>
                              {t("Approve Program")}
                            </Button>
                            <Button variant="outlined" color="error" startIcon={<CloseOutlinedIcon />} onClick={() => handleProgramApprovalAction(false)}>
                              {t("Reject Program")}
                            </Button>
                          </>
                        );
                      }

                      if (isClosed && isLeader) {
                        return (
                          <Button variant="outlined" startIcon={<PaidOutlinedIcon />} sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }} onClick={handleRequestWithdrawal}>
                            {t("Request Withdrawal")}
                          </Button>
                        );
                      }

                      // Placeholder for withdrawal approval actions
                      if (isClosed && isApprover) {
                        return (
                          <>
                            <Tooltip title={t("Approve Withdrawal Request")}>
                              <IconButton color="success" onClick={() => handleWithdrawalApprovalAction(true)}>
                                <DoneAllOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("Reject Withdrawal Request")}>
                              <IconButton color="error" onClick={() => handleWithdrawalApprovalAction(false)}>
                                <CloseOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        );
                      }

                      return null; // No actions available for this user/status
                    })()}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Typography color="text.secondary">
              {t("Select a program from the left to view details.")}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Create Program Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, color: brand }}>
            {t("Start Special Contribution Program")}
          </Typography>
          <IconButton onClick={handleCloseCreateDialog}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers ref={dialogContentRef}>
          <Collapse in={!!dialogError}>
            <Alert
              severity="error"
              onClose={() => setDialogError(null)}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <strong>{dialogError?.title}</strong> — {dialogError?.text}
            </Alert>
          </Collapse>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Define program details and select participants (subset or all members).")}
          </Typography>

          {/* Program details */}
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label={t("Title *")}
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("e.g., Mama Neema Delivery Support")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("Target per member (TZS) *")}
                    fullWidth
                    value={targetPerMember ? Number(targetPerMember).toLocaleString("en-US") : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/,/g, "");
                      if (!isNaN(val)) setTargetPerMember(val);
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Description")}
                    fullWidth
                    multiline
                    minRows={2}
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder={t("Short reason or context for this program...")}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("Start Date *")}
                    type="date"
                    fullWidth
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("End Date *")}
                    type="date"
                    fullWidth
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={3} display="flex" alignItems="center">
                  <Chip
                    icon={<PersonOutlineOutlinedIcon />}
                    label={`${t("Leader:")} ${user?.member_full_name}`}
                    sx={{ bgcolor: `${brand}14`, color: brand, fontWeight: 600 }}
                  />
                </Grid>

                <Grid item xs={12} md={3} display="flex" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectAll}
                        onChange={(e) => setSelectAll(e.target.checked)}
                      />
                    }
                    label={t("All Active Members")}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Summary strip */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography fontWeight={700} variant="body2" color="text.secondary">
                      {t("Selected (Active)")}
                    </Typography>
                    <Typography variant="h6" fontWeight={900}>{selectedActiveCount}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography fontWeight={700} variant="body2" color="text.secondary">
                      {t("Target / Member")}
                    </Typography>
                    <Typography variant="h6" fontWeight={900}>{formatCurrency((Number(targetPerMember) || 0), locale)} TZS</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography fontWeight={700} variant="body2" color="text.secondary">
                      {t("Total Target")}
                    </Typography>
                    <Typography variant="h6" fontWeight={900}>{formatCurrency(totalTarget, locale)} TZS</Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Participant selection */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" fontWeight={800}>
                  {t("Select Participants")}
                </Typography>
                <Tooltip title={t("Only active members are eligible to be charged. Inactive appear for transparency.")}>
                  <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Box>

              {!selectAll && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t("Search by name, phone, or role...")}
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            selectAll
                              ? true // If 'All' is toggled, checkbox is always checked
                              : filteredMembers.length > 0 && filteredMembers.filter(m => m.active).every(m => selectedIds.includes(m.id))
                          }
                          indeterminate={
                            !selectAll &&
                            filteredMembers.some(m => m.active && selectedIds.includes(m.id)) &&
                            !filteredMembers.filter(m => m.active).every(m => selectedIds.includes(m.id))
                          }
                          onChange={() => {
                            if (selectAll) {
                              return;
                            }
                            const activeFiltered = filteredMembers.filter(m => m.active).map(m => m.id);
                            const allSelected = activeFiltered.every(id => selectedIds.includes(id));
                            if (allSelected) {
                              setSelectedIds(prev => prev.filter(id => !activeFiltered.includes(id)));
                            } else {
                              setSelectedIds(prev => Array.from(new Set([...prev, ...activeFiltered])));
                            }
                          }}
                          disabled={selectAll}
                        />
                      </TableCell>
                      <TableCell>{t("Name")}</TableCell>
                      <TableCell>{t("Phone")}</TableCell>
                      <TableCell>{t("Role")}</TableCell>
                      <TableCell>{t("Status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectAll ? members : filteredMembers).map((m) => {
                      const isChecked = selectedIds.includes(m.id);
                      return (
                        <TableRow key={m.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleOne(m.id)}
                              disabled={selectAll || !m.is_active}
                            />
                          </TableCell>
                          <TableCell>{m.user.full_name}</TableCell>
                          <TableCell>{m.user.phone_number}</TableCell>
                          <TableCell>
                            <Chip label={m?.role} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={m.is_active ? t("Active") : t("Inactive")}
                              size="small"
                              color={m.is_active ? "success" : "default"}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!selectAll && filteredMembers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          {t("No members match your search.")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Button onClick={handleCloseCreateDialog}>{t("Cancel")}</Button>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddCheckOutlinedIcon />}
            sx={{ mr: 1, borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
            onClick={() => {
              Swal.fire({
                icon: "info",
                title: t("Validation Check"),
                html: `
                  <div style="text-align:left">
                    <strong>${t("Title:")}</strong> ${title || "(empty)"}<br/>
                    <strong>${t("Dates:")}</strong> ${start || "..."} → ${end || "..."}<br/>
                    <strong>${t("Active Participants:")}</strong> ${selectedActiveCount}<br/>
                    <strong>${t("Total Target:")}</strong> ${formatCurrency(totalTarget, locale)} TZS
                  </div>
                `,
                confirmButtonColor: brand,
              });
            }}
          >
            {t("Validate")}
          </Button>
          <Button
            variant="contained"
            startIcon={<GroupAddOutlinedIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Create Program")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdrawal Details Dialog */}
      <Dialog open={isWithdrawalDetailOpen} onClose={handleCloseWithdrawalDetail} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
            {t("Withdrawal Request Details")}
          </Typography>
          <IconButton onClick={handleCloseWithdrawalDetail}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProgram?.withdrawal_request ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography><strong>{t("Beneficiary:")}</strong> {selectedProgram.withdrawal_request.beneficiary_name}</Typography>
                <Typography><strong>{t("Account:")}</strong> {selectedProgram.withdrawal_request.beneficiary_account}</Typography>
                <Typography><strong>{t("Amount:")}</strong> {formatCurrency(selectedProgram.withdrawal_request.amount, locale)} TZS</Typography>
                {selectedProgram.withdrawal_request.purpose && <Typography><strong>{t("Purpose:")}</strong> {selectedProgram.withdrawal_request.purpose}</Typography>}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography><strong>{t("Status:")}</strong> <Chip {...withdrawalChipProps(selectedProgram.withdrawal_request.status, t)} size="small" /></Typography>
                {selectedProgram.withdrawal_request.status === 'REJECTED' && (
                  <Typography color="error.main" sx={{ mt: 1 }}>
                    <strong>{t("Rejection Reason:")}</strong> {selectedProgram.withdrawal_request.rejection_reason}
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t("Approval Chain")}</Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    {selectedProgram.withdrawal_request.chair_approved_by ? `✅ ${t("Chair: Approved")}` : `⏳ ${t("Chair: Pending")}`}
                  </Typography>
                  <Typography variant="body2">
                    {selectedProgram.withdrawal_request.secretary_approved_by ? `✅ ${t("Secretary: Approved")}` : `⏳ ${t("Secretary: Pending")}`}
                  </Typography>
                  <Typography variant="body2">
                    {selectedProgram.withdrawal_request.accountant_approved_by ? `✅ ${t("Accountant: Approved")}` : `⏳ ${t("Accountant: Pending")}`}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Typography>{t("No withdrawal details to display.")}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWithdrawalDetail}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
